import { createContext, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { normalizeDoctorType } from "../utils/utils";
import { useAuth } from "./useAuth";
import { NOTE_TYPES as CHIRO_NOTE_TYPES } from "../chiro/notes/catalogs/noteTypes";

// APIs
export const noteTypeContext = createContext();

function useNoteTypeProvider() {
  const { t } = useTranslation();
  const auth = useAuth();

  // Track custom note IDs for useMemo reactivity
  const customNotesKey = JSON.stringify([
    ...(auth?.auth?.user?.custom?.customNotes || []).map((n) => n.id),
    ...(auth?.auth?.orgInfo?.customNotes || []).map((n) => n.id),
  ]);

  // Object lucasAvailableNoteTypes
  const { noteTypes, noteTypesMap } = useMemo(() => {
    const initNotes = [
      {
        label: t("noteTypes.alf-home-new.label", "ALF/Home - Admit"),
        value: "alf-home-new",
        description: t("noteTypes.alf-home-new.description", "New patient visit for ALF Home"),
        icon: "ALF/Home<br>Admit",
        style: "info",
        pushTo: ["transition-care", "case-conference", "alf-home-followup", "code-rescue"],
        pushToDefault: "alf-home-followup",
        doctorTypes: ["homeHealth", "generalSubspecialty"],
        summaryType: "outpatient",
        priority: 2,
        supportWounds: true,
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.alf-home-followup.label", "ALF/Home - Follow-up"),
        value: "alf-home-followup",
        description: t("noteTypes.alf-home-followup.description", "Follow-up visit for ALF Home"),
        icon: "ALF/Home<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "alf-home-followup", "code-rescue"],
        pushToDefault: "alf-home-followup",
        doctorTypes: ["homeHealth", "generalSubspecialty"],
        summaryType: "outpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: true,
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.annual-wellness.label", "Annual - Wellness"),
        value: "annual-wellness",
        description: t("noteTypes.annual-wellness.description", "Annual Wellness note"),
        icon: "Annual<br>Wellness",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["primaryCare"],
        summaryType: "outpatient",
        priority: 2,
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.attending-att.label", "Attending Attestation"),
        value: "attending-att",
        description: t("noteTypes.attending-att.description", "Attending Attestation Note"),
        icon: "Attending<br>Attestation",
        style: "info",
        pushTo: ["transition-care", "case-conference", "attending-att-dc", "handoff"],
        pushToDefault: "attending-att-dc",
        doctorTypes: [
          "hospitalist",
          "internist",
          "er",
          "generalSurgery",
          "cardiologist",
          "podiatry",
          "nephrologist",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "behavioral",
        ],
        summaryType: "inpatient",
        priority: 3,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: false,
        updatePatientData: false,
      },
      {
        label: t("noteTypes.attending-att-dc.label", "Attending Attestation - DC Summary"),
        value: "attending-att-dc",
        description: t("noteTypes.attending-att-dc.description", "Attending Attestation Note - DC Summary"),
        icon: "Attending<br>Attestation DC",
        style: "info",
        pushTo: ["transition-care", "case-conference", "handoff"],
        doctorTypes: [
          "hospitalist",
          "internist",
          "er",
          "generalSurgery",
          "cardiologist",
          "podiatry",
          "nephrologist",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "behavioral",
        ],
        summaryType: "inpatient",
        priority: 3,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: false,
        updatePatientData: false,
      },
      {
        label: t("noteTypes.psych-birp.label", "BIRP"),
        value: "psych-birp",
        description: t("noteTypes.psych-birp.description", "Behavioral intervention response and plan note"),
        icon: "BIRP",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "psych-full-assessment",
          "psych-icanotes",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "psych-followup",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.consult-hp-cardiology.label", "Cardiology - Consult - HP"),
        value: "consult-hp-cardiology",
        description: t("noteTypes.consult-hp-cardiology.description", "Initial consult for cardiologist"),
        icon: "Consult<br>HP",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "consult-progress-cardiology",
          "diagnostic-cath",
          "patient-meeting",
          "clinic-new-cardiology",
          "clinic-followup-cardiology",
          "code-rescue",
          "handoff",
        ],
        pushToDefault: "consult-progress-cardiology",
        doctorTypes: ["cardiologist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.consult-progress-cardiology.label", "Cardiology - Consult - Progress"),
        value: "consult-progress-cardiology",
        description: t("noteTypes.consult-progress-cardiology.description", "Progress note for cardiologist"),
        icon: "Consult<br>Progress",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "consult-progress-cardiology",
          "diagnostic-cath",
          "patient-meeting",
          "clinic-new-cardiology",
          "clinic-followup-cardiology",
          "code-rescue",
          "handoff",
        ],
        pushToDefault: "consult-progress-cardiology",
        doctorTypes: ["cardiologist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.diagnostic-cath.label", "Cardiology - Diag. Cath"),
        value: "diagnostic-cath",
        description: t("noteTypes.diagnostic-cath.description", "Diagnostic Angiogram, no interventions"),
        icon: "Diagnostic<br>Cath",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic-new-cardiology",
          "clinic-followup-cardiology",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "clinic-followup-cardiology",
        doctorTypes: ["cardiologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.clinic-followup-cardiology.label", "Cardiology - Follow-up"),
        value: "clinic-followup-cardiology",
        description: t("noteTypes.clinic-followup-cardiology.description", "Returning clinic visit for cardiology"),
        icon: "Cardiology<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-cardiology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-cardiology",
        doctorTypes: ["cardiologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.clinic-new-cardiology.label", "Cardiology - New patient"),
        value: "clinic-new-cardiology",
        description: t("noteTypes.clinic-new-cardiology.description", "New patient clinic visit for cardiology"),
        icon: "Cardiology<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-cardiology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-cardiology",
        doctorTypes: ["cardiologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.case-conference.label", "Case Conference"),
        value: "case-conference",
        description: t("noteTypes.case-conference.description", "Case Conference"),
        style: "info",
        icon: "Case<br>Conf",
        pushTo: [
          "transition-care",
          "case-conference",
          "snf-new",
          "snf-progress",
          "snf-discharge",
          "soap",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "woundcare-home-visit",
          "alf-home-new",
          "alf-home-followup",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "case-conference",
        doctorTypes: ["homeHealth"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.concierge-medicine.label", "Concierge Medicine"),
        value: "concierge-medicine",
        description: t("noteTypes.concierge-medicine.description", "Concierge Medicine Visit - Primary Care with screenings"),
        icon: "visit",
        style: "info",
        pushTo: ["patient-meeting", "concierge-medicine", "clinic", "painmgmt-followup", "prior-auth", "code-rescue"],
        pushToDefault: "concierge-medicine",
        doctorTypes: ["physicalTherapist", "painmgmt", "primaryCare"],
      },
      {
        label: t("noteTypes.chat.label", "Chat"),
        value: "chat",
        style: "info",
        description: t("noteTypes.chat.description", "Patient initiated chat"),
        newEncounter: false,
        pushTo: [],
        doctorTypes: [],
        summaryType: "inpatient",
        priority: 3,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.code-rescue.label", "Code Rescue"),
        value: "code-rescue",
        description: t(
          "noteTypes.code-rescue.description",
          "Real-time documentation of critical events, such as code rescue and code blue with detailed audio to text transcription of participants, thoughts process, events, medications administered, and plan moving forward"
        ),
        icon: "Code<br>Rescue",
        style: "danger",
        pushTo: [
          "transition-care",
          "case-conference",
          "history",
          "progress",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "clinic-subspecialty",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "hospice-hp",
          "hospice-progress",
          "critical-care-hp",
          "critical-care-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "progress",
        doctorTypes: [
          "hospitalist",
          "internist",
          "er",
          "generalSurgery",
          "cardiologist",
          "podiatry",
          "nephrologist",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
        ],
        summaryType: "inpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.cpe.label", "Comprehensive Physical Exam"),
        value: "cpe",
        description: t("noteTypes.cpe.description", "Comprehensive Physical Exam note"),
        icon: "CPE",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["primaryCare"],
        summaryType: "outpatient",
        priority: 2,
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.consult.label", "Consult - HP"),
        value: "consult",
        description: t("noteTypes.consult.description", "Inpatient consult HP"),
        icon: "Consult<br>HP",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "history",
          "progress",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "consult-progress",
          "hospice-hp",
          "hospice-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "consult-progress",
        doctorTypes: ["hospitalist", "internist", "generalSurgery", "generalSubspecialty", "intensivist"],
        summaryType: "inpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.consult-progress.label", "Consult - Progress"),
        value: "consult-progress",
        description: t("noteTypes.consult-progress.description", "Inpatient progress note for consultants"),
        icon: "Consult<br>Progress",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "history",
          "progress",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "consult-progress",
          "hospice-hp",
          "hospice-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "consult-progress",
        doctorTypes: ["hospitalist", "internist", "generalSurgery", "generalSubspecialty", "intensivist"],
        summaryType: "inpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.critical-care-hp.label", "Critical Care - Admit"),
        value: "critical-care-hp",
        description: t("noteTypes.critical-care-hp.description", "Admission hp to the icu or step down"),
        icon: "Crit. Care<br>Admit",
        style: "danger",
        pushTo: [
          "transition-care",
          "case-conference",
          "progress",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "hospice-hp",
          "hospice-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "critical-care-progress",
        doctorTypes: ["hospitalist", "internist", "intensivist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.critical-care-progress.label", "Critical Care - Progress"),
        value: "critical-care-progress",
        description: t("noteTypes.critical-care-progress.description", "Progress Critical care progress note"),
        icon: "Crit. Care<br>Progress",
        style: "danger",
        pushTo: [
          "transition-care",
          "case-conference",
          "progress",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "hospice-hp",
          "hospice-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "critical-care-progress",
        doctorTypes: ["hospitalist", "internist", "intensivist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.dental-care-progress.label", "Dental - Care"),
        value: "dental-care-progress",
        description: t("noteTypes.dental-care-progress.description", "Progress note for dentist"),
        icon: "Dental<br>Care",
        style: "info",
        pushTo: ["dental-care-progress"],
        pushToDefault: "dental-care-progress",
        doctorTypes: ["dentist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.discseel-eval.label", "Discseel - Evaluation"),
        value: "discseel-eval",
        description: t("noteTypes.discseel-eval.description", "Evaluation for therapeutic intervention with Discseel"),
        icon: "Discseel<br>Eval",
        style: "info",
        pushTo: [
          "patient-meeting",
          "ortho-surgery-followup",
          "ortho-injury-followup",
          "neuro-ortho-spine-followup",
          "painmgmt-followup",
          "prior-auth",
          "code-rescue",
        ],
        pushToDefault: "painmgmt-followup",
        doctorTypes: ["orthopedicSurgeon", "painmgmt", "neuroOrthoSpine"],
      },
      {
        label: t("noteTypes.allergy-new.label", "Allergy - New Patient"),
        value: "allergy-new",
        description: t("noteTypes.allergy-new.description", "Allergy & Immunology new patient evaluation and documentation"),
        icon: "Allergy<br>New",
        style: "info",
        pushTo: ["patient-meeting", "allergy-followup", "procedure-generic", "code-rescue"],
        pushToDefault: "allergy-followup",
        doctorTypes: ["allergyImmunology"],
      },
      {
        label: t("noteTypes.allergy-followup.label", "Allergy - Follow Up"),
        value: "allergy-followup",
        description: t(
          "noteTypes.allergy-followup.description",
          "Allergy & Immunology follow-up visit and ongoing care documentation"
        ),
        icon: "Allergy<br>Followup",
        style: "info",
        pushTo: ["patient-meeting", "allergy-followup", "procedure-generic", "code-rescue"],
        pushToDefault: "allergy-followup",
        doctorTypes: ["allergyImmunology"],
      },
      {
        label: t("noteTypes.derm-followup.label", "Derm - Follow Up"),
        value: "derm-followup",
        description: t("noteTypes.derm-followup.description", "Dermatology follow-up visit and ongoing care documentation"),
        icon: "Derm<br>Followup",
        style: "info",
        pushTo: ["patient-meeting", "derm-followup", "derm-soap", "derm-procedure", "code-rescue"],
        pushToDefault: "derm-followup",
        doctorTypes: ["dermatology"],
      },
      {
        label: t("noteTypes.derm-new.label", "Derm - New Patient"),
        value: "derm-new",
        description: t("noteTypes.derm-new.description", "Dermatology new patient evaluation and documentation"),
        icon: "Derm<br>New",
        style: "info",
        pushTo: ["patient-meeting", "derm-followup", "derm-soap", "derm-procedure", "code-rescue"],
        pushToDefault: "derm-followup",
        doctorTypes: ["dermatology"],
      },
      {
        label: t("noteTypes.derm-procedure.label", "Derm - Procedure"),
        value: "derm-procedure",
        description: t(
          "noteTypes.derm-procedure.description",
          "Dermatology procedure documentation including minor surgeries, biopsies, or treatments"
        ),
        icon: "Derm<br>Procedure",
        style: "info",
        pushTo: ["patient-meeting", "derm-followup", "derm-soap", "derm-procedure", "code-rescue"],
        pushToDefault: "derm-followup",
        doctorTypes: ["dermatology"],
      },
      {
        label: t("noteTypes.derm-soap.label", "Derm - SOAP (Simplified)"),
        value: "derm-soap",
        description: t("noteTypes.derm-soap.description", "Simplified dermatology SOAP note for concise clinical documentation"),
        icon: "Derm<br>SOAP",
        style: "info",
        pushTo: ["patient-meeting", "derm-followup", "derm-soap", "derm-procedure", "code-rescue"],
        pushToDefault: "derm-followup",
        doctorTypes: ["dermatology"],
      },
      {
        label: t("noteTypes.endocrinology-followup.label", "Endocrine - Follow-up"),
        value: "endocrinology-followup",
        description: t("noteTypes.endocrinology-followup.description", "Returning clinic visit for endocrinology"),
        icon: "Endocr.<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "endocrinology-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "code-rescue",
        ],
        pushToDefault: "endocrinology-followup",
        doctorTypes: ["endocrinologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.endocrinology-new.label", "Endocrine - New patient"),
        value: "endocrinology-new",
        description: t("noteTypes.endocrinology-new.description", "New patient clinic visit for endocrinology"),
        icon: "Endocr.<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "endocrinology-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "code-rescue",
        ],
        pushToDefault: "endocrinology-followup",
        doctorTypes: ["endocrinologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ent-followup.label", "ENT - Follow-up"),
        value: "ent-followup",
        description: t("noteTypes.ent-followup.description", "Returning clinic visit for ENT"),
        icon: "ENT<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "ent-followup", "code-rescue"],
        pushToDefault: "ent-followup",
        doctorTypes: ["otolaryngologists"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ent-new.label", "ENT - New patient"),
        value: "ent-new",
        description: t("noteTypes.ent-new.description", "New patient clinic visit for ENT"),
        icon: "ENT<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "ent-followup", "code-rescue"],
        pushToDefault: "ent-followup",
        doctorTypes: ["otolaryngologists"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.er.label", "ER Note"),
        value: "er",
        description: t("noteTypes.er.description", "ER documentation"),
        icon: "ER",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue", "handoff"],
        pushToDefault: "code-rescue",
        doctorTypes: ["er"],
        summaryType: "er",
        supportWounds: true,
        supportUploadDocuments: true,
        sections: [
          {
            variable: "erPreHospital",
            label: "Pre-Hospital",
            divPrefix: "ERPre",
            processName: "NewNote - Inpatient - ER - Pre Hospital",
            cascade: ["chiefComplaint", "preHospital"],
          },
          {
            variable: "erHPI",
            label: "HPI/Initial ER-MDM",
            divPrefix: "ERHPI",
            processName: "NewNote - Inpatient - ER - HPI",
            cascade: [
              "chiefComplaint",
              "illnessHistory",
              "narrative",
              "pastMedicalHistory",
              "pastSurgeries",
              "allergies",
              "familyHistory",
              "socialHistory",
              "physicalExams",
              "initialErcoursemdm",
              "initialOrders",
            ],
          },
          {
            variable: "erReassessment",
            label: "Reassessment",
            divPrefix: "ERReassessment",
            multi: true,
            processName: "NewNote - Inpatient - ER - Reassessment",
            cascade: ["reassessErcoursemdm", "bedsideCardiacmonitor", "ekgInterpret", "wetImage"],
          },
          {
            variable: "erProcedure",
            label: "Procedure",
            divPrefix: "ERProcedure",
            multi: true,
            processName: "NewNote - Inpatient - ER - Procedure",
            cascade: ["procedureNote"],
          },
          {
            variable: "erCriticalCare",
            label: "Critical Care",
            divPrefix: "ERCriticalCare",
            processName: "NewNote - Inpatient - ER - Critical Care",
            cascade: ["criticalCareReason", "criticalCareInterventions", "criticalCareTime"],
          },
          {
            variable: "erDischarge",
            label: "Disposition: Discharge",
            divPrefix: "ERDischarge",
            processName: "NewNote - Inpatient - ER - Disposition",
            mutualExclusive: ["erAdmit", "erTransfer", "erHandoff"],
            cascade: [
              "dispoType",
              "dispoDischarge",
              "dispoDischargeOrders",
              "dispoDischargePrescriptions",
              "dispoAdmit",
              "dispoHandoff",
              "dispoTransfer",
              "dispoClinicalSummary",
              "dispoMdm",
              "dispoMdmPoints",
              "patientEducationAndDischargeInstructions",
              "followupCare",
            ],
          },
          {
            variable: "erAdmit",
            label: "Disposition: Admit",
            divPrefix: "ERAdmit",
            processName: "NewNote - Inpatient - ER - Disposition",
            mutualExclusive: ["erDischarge", "erTransfer", "erHandoff"],
            cascade: [
              "dispoType",
              "dispoDischarge",
              "dispoDischargeOrders",
              "dispoDischargePrescriptions",
              "dispoAdmit",
              "dispoHandoff",
              "dispoTransfer",
              "dispoClinicalSummary",
              "dispoMdm",
              "dispoMdmPoints",
              "patientEducationAndDischargeInstructions",
              "followupCare",
            ],
          },
          {
            variable: "erTransfer",
            label: "Disposition: Transfer",
            divPrefix: "ERTransfer",
            processName: "NewNote - Inpatient - ER - Disposition",
            mutualExclusive: ["erDischarge", "erAdmit", "erHandoff"],
            cascade: [
              "dispoType",
              "dispoDischarge",
              "dispoDischargeOrders",
              "dispoDischargePrescriptions",
              "dispoAdmit",
              "dispoHandoff",
              "dispoTransfer",
              "dispoClinicalSummary",
              "dispoMdm",
              "dispoMdmPoints",
              "patientEducationAndDischargeInstructions",
              "followupCare",
            ],
          },
          {
            variable: "erHandoff",
            label: "Disposition: Handoff",
            divPrefix: "ERHandoff",
            processName: "NewNote - Inpatient - ER - Disposition",
            mutualExclusive: ["erDischarge", "erAdmit", "erTransfer"],
            cascade: [
              "dispoType",
              "dispoDischarge",
              "dispoDischargeOrders",
              "dispoDischargePrescriptions",
              "dispoAdmit",
              "dispoHandoff",
              "dispoTransfer",
              "dispoClinicalSummary",
              "dispoMdm",
              "dispoMdmPoints",
              "patientEducationAndDischargeInstructions",
              "followupCare",
            ],
          },
        ],
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.procedure-egd.label", "Gastroenterology - EGD"),
        value: "procedure-egd",
        description: t("noteTypes.procedure-egd.description", "Gastroenterology EGD Procedure"),
        icon: "GI<br>EGD",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-gastroenterology", "patient-meeting", "code-rescue"],
        doctorTypes: ["gastroenterologist", "generalSurgery"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.clinic-followup-gastroenterology.label", "Gastroenterology - Follow-up"),
        value: "clinic-followup-gastroenterology",
        description: t("noteTypes.clinic-followup-gastroenterology.description", "Returning clinic visit for gastroenterology"),
        icon: "GI<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-gastroenterology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-gastroenterology",
        doctorTypes: ["gastroenterologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.clinic-new-gastroenterology.label", "Gastroenterology - New patient"),
        value: "clinic-new-gastroenterology",
        description: t("noteTypes.clinic-new-gastroenterology.description", "New patient clinic visit for gastroenterology"),
        icon: "GI<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-gastroenterology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-gastroenterology",
        doctorTypes: ["gastroenterologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.psych-girp.label", "GIRP"),
        value: "psych-girp",
        description: t("noteTypes.psych-girp.description", "Behavioral goals intervention response and plan note"),
        icon: "GIRP",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "psych-full-assessment",
          "psych-icanotes",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "psych-followup",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.gynecology-followup.label", "Gynecology - Follow-up"),
        value: "gynecology-followup",
        description: t("noteTypes.gynecology-followup.description", "Returning clinic visit for Gynecology"),
        icon: "Gynecology<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "gynecology-followup", "code-rescue"],
        pushToDefault: "gynecology-followup",
        doctorTypes: ["obgyn"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.gynecology-new.label", "Gynecology - New patient"),
        value: "gynecology-new",
        description: t("noteTypes.gynecology-new.description", "New patient clinic visit for Gynecology"),
        icon: "Gynecology<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "gynecology-followup", "code-rescue"],
        pushToDefault: "gynecology-followup",
        doctorTypes: ["obgyn"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.handoff.label", "Handoff"),
        value: "handoff",
        icon: "HandOff",
        style: "info",
        description: t("noteTypes.handoff.description", "Hand off of encounter"),
        newEncounter: false,
        pushTo: [
          "transition-care",
          "case-conference",
          "history",
          "progress",
          "discharge",
          "death",
          "transfer",
          "rehab-hp",
          "rehab-progress",
          "ltach-hp",
          "ltach-progress",
          "ltach-discharge",
          "consult",
          "consult-progress",
          "hospice-hp",
          "hospice-progress",
          "hospice-recert",
          "attending-att",
          "attending-att-dc",
          "snf-new",
          "snf-progress",
          "snf-discharge",
          "consult-hp-nephrology",
          "consult-progress-nephrology",
          "consult-hp-cardiology",
          "consult-progress-cardiology",
          "pulmonology-consult-hp",
          "pulmonology-consult-progress",
          "consult-hp-podiatry",
          "consult-progress-podiatry",
          "critical-care-hp",
          "critical-care-progress",
          "er",
          "surgery-hp",
          "surgery-progress",
          "patient-meeting",
          "code-rescue",
          "prior-auth",
        ],
        doctorTypes: [],
        summaryType: "inpatient",
        priority: 3,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.hospice-hp.label", "Hospice - Admit"),
        value: "hospice-hp",
        description: t("noteTypes.hospice-hp.description", "Admission to Hospice IPU"),
        icon: "Hospice<br>Admit",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "patient-meeting",
          "code-rescue",
          "hospice-progress",
          "hospice-his",
          "hospice-discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "hospice-progress",
        doctorTypes: ["hospitalist", "internist"],
        summaryType: "inpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.hospice-discharge.label", "Hospice - Discharge"),
        value: "hospice-discharge",
        description: t("noteTypes.hospice-discharge.description", "Discharge note for Hospice patients"),
        icon: "Hospice<br>Discharge",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "patient-meeting",
          "code-rescue",
          "hospice-admit",
          "hospice-progress",
          "hospice-his",
          "handoff",
        ],
        pushToDefault: "hospice-admit",
        doctorTypes: ["hospitalist", "internist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.hospice-his.label", "Hospice - HIS"),
        value: "hospice-his",
        description: t("noteTypes.hospice-his.description", "Hospice HIS note"),
        icon: "Hospice<br>HIS",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "patient-meeting",
          "code-rescue",
          "hospice-his",
          "hospice-progress",
          "hospice-discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "hospice-his",
        doctorTypes: ["hospitalist", "internist"],
        summaryType: "outpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.hospice-progress.label", "Hospice - Progress"),
        value: "hospice-progress",
        description: t("noteTypes.hospice-progress.description", "Progress note for Hospice patients"),
        icon: "Hospice<br>Progress",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "patient-meeting",
          "code-rescue",
          "hospice-progress",
          "hospice-his",
          "hospice-discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "hospice-progress",
        doctorTypes: ["hospitalist", "internist"],
        summaryType: "inpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.hospice-recert.label", "Hospice - Recertification"),
        value: "hospice-recert",
        description: t("noteTypes.hospice-recert.description", "Recertification note for Hospice patients"),
        icon: "Hospice<br>Recert",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "hospice-progress",
          "hospice-his",
          "hospice-discharge",
          "code-rescue",
          "handoff",
        ],
        doctorTypes: ["hospitalist", "internist"],
        priority: 2,
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.infection-initial.label", "Infection Disease - Consult Initial"),
        value: "infection-initial",
        description: t("noteTypes.infection-initial.description", "Initial intake visit for Infectious Diseases"),
        icon: "Infection<br>Initial",
        style: "info",
        pushTo: ["transition-care", "case-conference", "infection-progress", "patient-meeting", "code-rescue"],
        pushToDefault: "infection-progress",
        doctorTypes: ["infectiousDiseases"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.infection-progress.label", "Infection Disease - Consult Progress"),
        value: "infection-progress",
        description: t("noteTypes.infection-progress.description", "Progress note for Infectious Diseases"),
        icon: "Infection<br>Progress",
        style: "info",
        pushTo: ["transition-care", "case-conference", "infection-progress", "patient-meeting", "code-rescue"],
        pushToDefault: "infection-progress",
        doctorTypes: ["infectiousDiseases"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.infection-followup.label", "Infection Disease - Follow-up"),
        value: "infection-followup",
        description: t("noteTypes.infection-followup.description", "Returning clinic visit for Infectious Diseases"),
        icon: "Infection<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "infection-followup", "code-rescue"],
        pushToDefault: "infection-followup",
        doctorTypes: ["infectiousDiseases"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.infection-new.label", "Infection Disease - New patient"),
        value: "infection-new",
        description: t("noteTypes.infection-new.description", "New patient clinic visit for Infectious Diseases"),
        icon: "Infection<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "infection-followup", "code-rescue"],
        pushToDefault: "infection-followup",
        doctorTypes: ["infectiousDiseases"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ivim-therapy.label", "IV/IM Therapy"),
        value: "ivim-therapy",
        description: t("noteTypes.ivim-therapy.description", "Documentation of IV/IM procedures"),
        icon: "Therapy<br>IV/IM",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue"],
        doctorTypes: ["primaryCare", "internist"],
        summaryType: "outpatient",
        priority: 3,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.ltach-hp.label", "LTACH - Admit"),
        value: "ltach-hp",
        description: t("noteTypes.ltach-hp.description", "LTACH HP medical note"),
        icon: "LTACH<br>Admit",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ltach-progress",
          "ltach-discharge",
          "death",
          "patient-meeting",
          "code-rescue",
          "handoff",
        ],
        doctorTypes: ["internist"],
        summaryType: "inpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ltach-discharge.label", "LTACH - Discharge"),
        value: "ltach-discharge",
        description: t("noteTypes.ltach-discharge.description", "Discharge LTACH medical note"),
        icon: "LTACH<br>Discharge",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ltach-progress",
          "ltach-discharge",
          "death",
          "patient-meeting",
          "code-rescue",
          "handoff",
        ],
        doctorTypes: ["internist"],
        summaryType: "inpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.ltach-progress.label", "LTACH - Progress"),
        value: "ltach-progress",
        description: t("noteTypes.ltach-progress.description", "LTACH progress note"),
        icon: "LTACH<br>Progress",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ltach-progress",
          "ltach-discharge",
          "death",
          "patient-meeting",
          "code-rescue",
          "handoff",
        ],
        doctorTypes: ["internist"],
        summaryType: "inpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.history.label", "Medical - Admit"),
        value: "history",
        description: t("noteTypes.history.description", "Generic inpatient medical admission note"),
        icon: "Medical<br>Admit",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "critical-care-hp",
          "progress",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "hospice-hp",
          "hospice-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "progress",
        doctorTypes: ["hospitalist", "internist", "intensivist"],
        summaryType: "inpatient",
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.death.label", "Medical - Discharge - Death"),
        value: "death",
        description: t("noteTypes.death.description", "Discharge summary for expired patients"),
        icon: "Discharge<br>Death",
        style: "info",
        doctorTypes: ["hospitalist", "internist", "intensivist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.discharge.label", "Medical - Discharge - Summary"),
        value: "discharge",
        description: t("noteTypes.discharge.description", "Discharge inpatient medical note"),
        icon: "Discharge<br>Summary",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "progress",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "hospice-hp",
          "hospice-progress",
          "transfer",
          "death",
          "handoff",
        ],
        doctorTypes: ["hospitalist", "internist", "intensivist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.transfer.label", "Medical - Discharge - Transfer"),
        value: "transfer",
        description: t("noteTypes.transfer.description", "Transfer to outside facility"),
        icon: "Discharge<br>Transfer",
        style: "info",
        doctorTypes: ["hospitalist", "internist", "intensivist"],
        pushTo: ["transition-care", "case-conference", "code-rescue", "handoff"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.progress.label", "Medical - Progress"),
        value: "progress",
        description: t("noteTypes.progress.description", "Generic inpatient medical progress note"),
        icon: "Medical<br>Progress",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "progress",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "hospice-hp",
          "hospice-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        pushToDefault: "progress",
        doctorTypes: ["hospitalist", "internist", "intensivist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.medicare-wellness-initial.label", "Medicare - Wellness Initial"),
        value: "medicare-wellness-initial",
        description: t("noteTypes.medicare-wellness-initial.description", "Medicare Wellness Initial note"),
        icon: "Medicare<br>Initial",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "medicare-wellness",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["primaryCare"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.medicare-wellness.label", "Medicare - Wellness Subsequent"),
        value: "medicare-wellness",
        description: t("noteTypes.medicare-wellness.description", "Medicare Wellness Subsequent note"),
        icon: "Medicare<br>Subq",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "medicare-wellness",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["primaryCare"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.medicare-wellness-welcome.label", "Medicare - Wellness Welcome"),
        value: "medicare-wellness-welcome",
        description: t("noteTypes.medicare-wellness-welcome.description", "Medicare Wellness - Welcome note"),
        icon: "Medicare<br>Welcome",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "medicare-wellness",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["primaryCare"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.consult-hp-nephrology.label", "Nephrology - Consult - HP"),
        value: "consult-hp-nephrology",
        description: t("noteTypes.consult-hp-nephrology.description", "Initial consult for nephrologist"),
        icon: "Consult<br>HP",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "consult-progress-nephrology",
          "patient-meeting",
          "clinic-new-nephrology",
          "clinic-followup-nephrology",
          "code-rescue",
          "handoff",
        ],
        pushToDefault: "consult-progress-nephrology",
        doctorTypes: ["nephrologist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.consult-progress-nephrology.label", "Nephrology - Consult - Progress"),
        value: "consult-progress-nephrology",
        description: t("noteTypes.consult-progress-nephrology.description", "Progress note for nephrologist"),
        icon: "Consult<br>Progress",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "consult-progress-nephrology",
          "patient-meeting",
          "clinic-new-nephrology",
          "clinic-followup-nephrology",
          "code-rescue",
          "handoff",
        ],
        pushToDefault: "consult-progress-nephrology",
        doctorTypes: ["nephrologist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.clinic-followup-nephrology.label", "Nephrology - Follow-up"),
        value: "clinic-followup-nephrology",
        description: t("noteTypes.clinic-followup-nephrology.description", "Returning clinic visit for nephrology"),
        icon: "Nephrology<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-nephrology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-nephrology",
        doctorTypes: ["nephrologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.clinic-new-nephrology.label", "Nephrology - New patient"),
        value: "clinic-new-nephrology",
        description: t("noteTypes.clinic-new-nephrology.description", "New patient clinic visit for nephrology"),
        icon: "Nephrology<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-nephrology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-nephrology",
        doctorTypes: ["nephrologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.neuro-followup.label", "Neuro - Follow-up"),
        value: "neuro-followup",
        description: t("noteTypes.neuro-followup.description", "Neurologist followup note"),
        icon: "Neuro<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "neuro-followup", "code-rescue"],
        pushToDefault: "neuro-followup",
        doctorTypes: ["neurologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.neuro-new.label", "Neuro - New patient"),
        value: "neuro-new",
        description: t("noteTypes.neuro-new.description", "Neurologist new patient note"),
        icon: "Neuro<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "neuro-followup", "code-rescue"],
        pushToDefault: "neuro-followup",
        doctorTypes: ["neurologist"],
        summaryType: "outpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.neuro-telestroke.label", "Neuro - TeleStroke"),
        value: "neuro-telestroke",
        description: t("noteTypes.neuro-telestroke.description", "Neurology telestroke note"),
        icon: "Neuro<br>TeleStroke",
        style: "info",
        pushTo: [],
        doctorTypes: ["neurologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.neuro-ortho-spine-followup.label", "Neuro/Ortho Spine - Follow-up"),
        value: "neuro-ortho-spine-followup",
        description: t(
          "noteTypes.neuro-ortho-spine-followup.description",
          "Returning clinic visit for Neuro Ortho Spine surgeons"
        ),
        icon: "Neu/Ortho<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "code-rescue",
          "patient-meeting",
        ],
        pushToDefault: "neuro-ortho-spine-followup",
        pushVariables: {
          incidentDate: "local:onsetDate",
        },
        doctorTypes: ["neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.neuro-ortho-spine-new.label", "Neuro/Ortho Spine - New patient"),
        value: "neuro-ortho-spine-new",
        description: t("noteTypes.neuro-ortho-spine-new.description", "New patient clinic visit for Neuro Ortho Spine surgeons"),
        icon: "Neu/Ortho<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "code-rescue",
          "patient-meeting",
        ],
        pushToDefault: "neuro-ortho-spine-followup",
        pushVariables: {
          incidentDate: "local:onsetDate",
        },
        doctorTypes: ["neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.neuro-ortho-spine-preop.label", "Neuro/Ortho Spine - Pre-Op Eval"),
        value: "neuro-ortho-spine-preop",
        description: t(
          "noteTypes.neuro-ortho-spine-preop.description",
          "Pre operation evaluation for Neuro Ortho Spine surgeons"
        ),
        icon: "Neu/Ortho Spine<br>Pre-Op",
        style: "info",
        pushTo: ["transition-care", "case-conference", "neuro-ortho-spine-followup", "code-rescue", "patient-meeting"],
        pushToDefault: "neuro-ortho-spine-followup",
        pushVariables: {
          incidentDate: "local:onsetDate",
        },
        doctorTypes: ["neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.oasis-pt-soc.label", "Oasis - PT SoC"),
        value: "oasis-pt-soc",
        description: t("noteTypes.oasis-pt-soc.description", "OASIS Physical Therapy Start Of Care note"),
        style: "info",
        icon: "OASIS<br>PT SoC",
        pushTo: ["transition-care", "case-conference", "oasis-pt-soc"],
        pushToDefault: "oasis-pt-soc",
        doctorTypes: ["homeHealth"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.oasis-start.label", "Oasis - SoC"),
        value: "oasis-start",
        description: t("noteTypes.oasis-start.description", "OASIS Start Of Care note"),
        style: "info",
        icon: "OASIS<br>SoC",
        pushTo: ["transition-care", "case-conference", "oasis-start"],
        pushToDefault: "oasis-start",
        doctorTypes: ["homeHealth"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.obstetrics-followup.label", "Obstetrics - Follow-up"),
        value: "obstetrics-followup",
        description: t("noteTypes.obstetrics-followup.description", "Returning clinic visit for Obstetrics"),
        icon: "Obstetrics<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "obstetrics-followup", "code-rescue"],
        pushToDefault: "obstetrics-followup",
        doctorTypes: ["obgyn"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.obstetrics-new.label", "Obstetrics - New patient"),
        value: "obstetrics-new",
        description: t("noteTypes.obstetrics-new.description", "New patient clinic visit for Obstetrics"),
        icon: "Obstetrics<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "obstetrics-followup", "code-rescue"],
        pushToDefault: "obstetrics-followup",
        doctorTypes: ["obgyn"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ortho-injury-followup.label", "Orthopedic Injury - Follow-up"),
        value: "ortho-injury-followup",
        description: t("noteTypes.ortho-injury-followup.description", "Orthopedic Injury follow up note"),
        icon: "Ortho<br>Follow-Up",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ortho-injury-followup",
          "ortho-surgery-followup",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "code-rescue",
          "patient-meeting",
        ],
        pushToDefault: "ortho-injury-followup",
        doctorTypes: ["orthopedicSurgeon", "neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ortho-injury-new.label", "Orthopedic Injury - New Patient"),
        value: "ortho-injury-new",
        description: t("noteTypes.ortho-injury-new.description", "Orthopedic Injury new patient note"),
        icon: "Ortho<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ortho-injury-followup",
          "ortho-surgery-followup",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "code-rescue",
          "patient-meeting",
        ],
        pushToDefault: "ortho-injury-followup",
        doctorTypes: ["orthopedicSurgeon", "neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ortho-surgery-preop.label", "Orthopedic Injury - Pre-op"),
        value: "ortho-surgery-preop",
        description: t("noteTypes.ortho-surgery-preop.description", "Orthopedic Injury pre operation note"),
        icon: "Ortho<br>Pre-op",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ortho-injury-followup",
          "ortho-surgery-followup",
          "code-rescue",
          "patient-meeting",
        ],
        pushToDefault: "ortho-injury-followup",
        doctorTypes: ["orthopedicSurgeon", "neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ortho-surgery-followup.label", "Orthopedic Surgery - Follow-up"),
        value: "ortho-surgery-followup",
        description: t("noteTypes.ortho-surgery-followup.description", "Orthopedic Surgery follow up note"),
        icon: "Ortho<br>Follow-Up",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ortho-surgery-followup",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "code-rescue",
          "patient-meeting",
        ],
        pushToDefault: "ortho-surgery-followup",
        doctorTypes: ["orthopedicSurgeon", "neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ortho-surgery-new.label", "Orthopedic Surgery - New Patient"),
        value: "ortho-surgery-new",
        description: t("noteTypes.ortho-surgery-new.description", "Orthopedic Surgery new patient note"),
        icon: "Ortho<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "ortho-surgery-followup",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "code-rescue",
          "patient-meeting",
        ],
        pushToDefault: "ortho-surgery-followup",
        doctorTypes: ["orthopedicSurgeon", "neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.neuro-ortho-spine-followup-complex.label", "Ortho-spine - complex "),
        value: "neuro-ortho-spine-followup-complex",
        description: t("noteTypes.neuro-ortho-spine-followup-complex.description", "Complex Follow up for specialist"),
        icon: "Followup",
        style: "info",
        pushTo: [
          "patient-meeting",
          "ortho-surgery-followup",
          "ortho-injury-followup",
          "neuro-ortho-spine-followup",
          "painmgmt-followup",
          "prior-auth",
          "code-rescue",
        ],
        pushToDefault: "painmgmt-followup",
        doctorTypes: ["orthopedicSurgeon", "neuroOrthoSpine", "painmgmt"],
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.ortho-surgery-postop.label", "Orthopedic Surgery - Post-op"),
        value: "ortho-surgery-postop",
        description: t("noteTypes.ortho-surgery-postop.description", "Orthopedic Surgery post operation note"),
        icon: "Ortho<br>Post-op",
        style: "info",
        pushTo: ["transition-care", "case-conference", "ortho-surgery-followup", "code-rescue", "patient-meeting"],
        pushToDefault: "ortho-surgery-followup",
        doctorTypes: ["orthopedicSurgeon", "neuroOrthoSpine"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.painmgmt-followup.label", "Pain Mgmt - Follow-up"),
        value: "painmgmt-followup",
        description: t("noteTypes.painmgmt-followup.description", "Follow-up visit for pain management"),
        icon: "Pain<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "painmgmt-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "code-rescue",
        ],
        pushToDefault: "painmgmt-followup",
        doctorTypes: ["painmgmt"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: false,
        updatePatientData: true,
      },
      {
        label: t("noteTypes.painmgmt-new.label", "Pain Mgmt - New patient"),
        value: "painmgmt-new",
        description: t("noteTypes.painmgmt-new.description", "New patient visit for pain management"),
        icon: "Pain<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "painmgmt-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "code-rescue",
        ],
        pushToDefault: "painmgmt-followup",
        doctorTypes: ["painmgmt"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: false,
        updatePatientData: true,
      },
      {
        label: t("noteTypes.paramedics.label", "Paramedics Note"),
        value: "paramedics",
        description: t("noteTypes.paramedics.description", "Paramedics documentation"),
        icon: "Medics",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue", "handoff"],
        pushToDefault: "code-rescue",
        doctorTypes: ["paramedics"],
        summaryType: "paramedics",
        supportWounds: true,
        supportUploadDocuments: true,
        sections: [
          {
            variable: "paramedicsInitial",
            label: "Scene/Initial Assessment",
            divPrefix: "ParamedicsInitial",
            processName: "NewNote - Inpatient - Paramedics - Initial",
          },
          {
            variable: "paramedicsReassessment",
            label: "Reassessment / Intervention",
            divPrefix: "ParamedicsReassessment",
            multi: true,
            processName: "NewNote - Inpatient - Paramedics - Reassessment",
          },
          {
            variable: "paramedicsCriticalCare",
            label: "Critical Care",
            divPrefix: "ParamedicsCriticalCare",
            processName: "NewNote - Inpatient - Paramedics - Critical Care",
            cascade: ["criticalCareReason", "criticalCareInterventions", "criticalCareTime"],
          },
          {
            variable: "paramedicsTransfer",
            label: "Disposition: Transfer",
            divPrefix: "ParamedicsTransfer",
            processName: "NewNote - Inpatient - Paramedics - Disposition",
            mutualExclusive: ["paramedicsCleared", "paramedicsRefusal"],
            cascade: ["dispoType", "paramedicsCleared", "paramedicsTransfer"],
          },
          {
            variable: "paramedicsCleared",
            label: "Disposition: Cleared",
            divPrefix: "ParamedicsCleared",
            processName: "NewNote - Inpatient - Paramedics - Disposition",
            mutualExclusive: ["paramedicsTransfer", "paramedicsRefusal"],
            cascade: ["dispoType", "paramedicsCleared", "paramedicsTransfer"],
          },
          {
            variable: "paramedicsRefusal",
            label: "Disposition: Refusal",
            divPrefix: "ParamedicsRefusal",
            processName: "NewNote - Inpatient - Paramedics - Disposition",
            mutualExclusive: ["paramedicsTransfer", "paramedicsTransfer"],
            cascade: ["dispoType", "paramedicsCleared", "paramedicsTransfer"],
          },
        ],
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.patient-meeting.label", "Patient Meeting"),
        value: "patient-meeting",
        description: t(
          "noteTypes.patient-meeting.description",
          "Document any important discussions, example risk and benefits, procedure consent, AMA, plan of care, goals of care"
        ),
        icon: "Patient<br>Meeting",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "history",
          "progress",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "clinic-subspecialty",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
          "patient-meeting",
          "code-rescue",
          "critical-care-progress",
          "hospice-hp",
          "hospice-progress",
          "discharge",
          "transfer",
          "death",
          "handoff",
        ],
        doctorTypes: [
          "hospitalist",
          "internist",
          "er",
          "generalSurgery",
          "cardiologist",
          "podiatry",
          "nephrologist",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "behavioral",
          "neuroOrthoSpine",
          "radonc",
          "rheumatologist",
        ],
        summaryType: "inpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: false,
      },
      {
        label: t("noteTypes.pediatric-followup.label", "Pediatric - Follow-up"),
        value: "pediatric-followup",
        description: t("noteTypes.pediatric-followup.description", "Follow-up visit for pediatric"),
        icon: "Pediatric<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "pediatric-followup",
          "pediatric-wellchild",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "pediatric-followup",
        doctorTypes: ["pediatrician"],
        summaryType: "outpatient",
        supportWounds: true,
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.pediatric-new.label", "Pediatric - New patient"),
        value: "pediatric-new",
        description: t("noteTypes.pediatric-new.description", "New patient visit for pediatric"),
        icon: "Pediatric<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "pediatric-followup",
          "pediatric-wellchild",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "pediatric-followup",
        doctorTypes: ["pediatrician"],
        summaryType: "outpatient",
        supportWounds: true,
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.pediatric-wellchild-followup.label", "Pediatric WellChild - Follow-up"),
        value: "pediatric-wellchild-followup",
        description: t("noteTypes.pediatric-wellchild-followup.description", "Pediatric well child follow-up visit"),
        icon: "Well-Child<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "pediatric-followup",
          "pediatric-wellchild-followup",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "pediatric-wellchild-followup",
        doctorTypes: ["pediatrician"],
        summaryType: "outpatient",
        supportWounds: true,
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.pediatric-wellchild-new.label", "Pediatric WellChild - New"),
        value: "pediatric-wellchild-new",
        description: t("noteTypes.pediatric-wellchild-new.description", "Pediatric well child new visit"),
        icon: "Well-Child<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "pediatric-followup",
          "pediatric-wellchild-followup",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "pediatric-wellchild-followup",
        doctorTypes: ["pediatrician"],
        summaryType: "outpatient",
        supportWounds: true,
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.clinic-phone.label", "Phone"),
        value: "clinic-phone",
        description: t("noteTypes.clinic-phone.description", "Phone message/conversation documentation"),
        icon: "Clinic<br>Phone",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
        ],
        pushToDefault: "clinic",
        doctorTypes: [
          "primaryCare",
          "internist",
          "generalSurgery",
          "cardiologist",
          "nephrologist",
          "podiatry",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "radonc",
        ],
        summaryType: "outpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.pt-followup.label", "Physical Therapy - Follow-up"),
        value: "pt-followup",
        description: t("noteTypes.pt-followup.description", "Follow-up visit for physical therapy"),
        icon: "PT<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue"],
        doctorTypes: ["physicalTherapist"],
        summaryType: "outpatient",
        updatePatientData: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.pt-new.label", "Physical Therapy - New patient"),
        value: "pt-new",
        description: t("noteTypes.pt-new.description", "New patient visit for physical therapy"),
        icon: "PT<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue"],
        doctorTypes: ["physicalTherapist"],
        summaryType: "outpatient",
        updatePatientData: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.consult-hp-podiatry.label", "Podiatry - Consult - HP"),
        value: "consult-hp-podiatry",
        description: t("noteTypes.consult-hp-podiatry.description", "Initial consult for podiatry"),
        icon: "Consult<br>HP",
        style: "info",
        pushTo: ["transition-care", "case-conference", "consult-progress-podiatry", "code-rescue", "handoff"],
        doctorTypes: ["podiatry"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.consult-progress-podiatry.label", "Podiatry - Consult - Progress"),
        value: "consult-progress-podiatry",
        description: t("noteTypes.consult-progress-podiatry.description", "Progress note for podiatrist"),
        icon: "Consult<br>Progress",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue", "handoff"],
        doctorTypes: ["podiatry"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.clinic-followup-podiatry.label", "Podiatry - Follow-up"),
        value: "clinic-followup-podiatry",
        description: t("noteTypes.clinic-followup-podiatry.description", "Returning clinic visit for podiatry"),
        icon: "Clinic<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-podiatry", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-podiatry",
        doctorTypes: ["podiatry"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: true,
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.clinic-new-podiatry.label", "Podiatry - New patient"),
        value: "clinic-new-podiatry",
        description: t("noteTypes.clinic-new-podiatry.description", "New patient clinic visit for podiatry"),
        icon: "Clinic<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-podiatry", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-podiatry",
        doctorTypes: ["podiatry"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: true,
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.clinic.label", "Primary Care - Follow-up"),
        value: "clinic",
        description: t("noteTypes.clinic.description", "Generic clinic note for acute visits, follow-up, health maintenance"),
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
          "concierge-medicine",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["primaryCare", "internist"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        // Mirrors backend `DynaFormat - Outpatient - Primary Care - Follow up.json` section list.
        skeletonTitles: [
          "Chief Complaint",
          "History of Present Illness",
          "Past Medical History",
          "Past Surgical History",
          "Allergies",
          "Current Medications",
          "Family History",
          "Social History",
          "Review of Systems (ROS)",
          "Physical Exams",
          "Assessment and Plan",
          "Problem List",
          "Plan Of Care",
          "Prevention and Wellness",
          "Education/Counseling",
          "MDM",
          "Follow up",
          "Summary of Prescriptions and Orders",
          "Checklist",
          "Patient complexity",
          "Level of Service",
          "Office Procedures",
          "CPT Level II / HCPCS Codes",
          "ICD 10 Codes",
          "Time Spent",
        ],
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.clinic-new.label", "Primary Care - New patient"),
        value: "clinic-new",
        description: t("noteTypes.clinic-new.description", "New patient note"),
        icon: "Clinic<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
          "concierge-medicine",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["primaryCare", "internist"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.prior-auth.label", "Prior Authorization"),
        value: "prior-auth",
        icon: "Prior<br>Auth",
        style: "info",
        pushTo: ["handoff"],
        description: t("noteTypes.prior-auth.description", "Request for Prior Authorization"),
        doctorTypes: [
          "hospitalist",
          "internist",
          "er",
          "primaryCare",
          "generalSurgery",
          "cardiologist",
          "podiatry",
          "nephrologist",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "neuroOrthoSpine",
          "radonc",
          "orthopedicSurgeon",
          "rheumatologist",
        ],
        summaryType: "inpatient",
        priority: 3,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.procedure-generic.label", "Procedure - Generic"),
        value: "procedure-generic",
        description: t("noteTypes.procedure-generic.description", "Generic procedure"),
        style: "info",
        icon: "Procedure<br>Generic",
        pushTo: "*",
        pushToDefault: "procedure-generic",
        doctorTypes: "*",
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.woundcare-procedure.label", "Procedure - Wound Care"),
        value: "woundcare-procedure",
        description: t("noteTypes.woundcare-procedure.description", "Procedure for wound care"),
        icon: "Wound<br>Procedure",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "woundcare-procedure",
          "woundcare-home-visit",
          "woundcare-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "woundcare-followup",
        doctorTypes: ["woundCare", "generalSurgery", "podiatry"],
        supportWounds: true,
        summaryType: "outpatient",
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.psych-followup.label", "Psych - Follow-up"),
        value: "psych-followup",
        description: t("noteTypes.psych-followup.description", "Behavioral followup note"),
        icon: "Psych<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "psych-full-assessment",
          "psych-icanotes",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "psych-followup",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.psych-full-assessment.label", "Psych - Full Assessment"),
        value: "psych-full-assessment",
        description: t("noteTypes.psych-full-assessment.description", "Psych Full Assessment"),
        icon: "Full As.",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-full-assessment",
          "psych-icanotes",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "psych-full-assessment",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.psych-icanotes.label", "Psych - ICANotes"),
        value: "psych-icanotes",
        description: t("noteTypes.psych-icanotes.description", "Psych ICANotes"),
        icon: "ICANotes",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-icanotes",
          "psych-full-assessment",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "psych-icanotes",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.psych-intake.label", "Psych - Intake"),
        value: "psych-intake",
        description: t("noteTypes.psych-intake.description", "Behavioral intake note"),
        icon: "Psych<br>Intake",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "psych-full-assessment",
          "psych-icanotes",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "psych-followup",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.pt-assessment.label", "PTA note"),
        value: "pt-assessment",
        description: t("noteTypes.pt-assessment.description", "Physical Therapy Assessment"),
        icon: "PTA",
        style: "info",
        pushTo: ["patient-meeting", "pt-followup", "pt-new", "clinic", "painmgmt-followup", "prior-auth", "code-rescue"],
        pushToDefault: "painmgmt-followup",
        doctorTypes: ["physicalTherapist", "painmgmt", "primaryCare"],
      },
      {
        label: t("noteTypes.procedure-bronch.label", "Pulmonology - Bronchoscopy"),
        value: "procedure-bronch",
        description: t("noteTypes.procedure-bronch.description", "Bronchoscopy procedure"),
        icon: "Procedure<br>Bronch.",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "consult-progress",
          "clinic-subspecialty",
          "critical-care-hp",
          "critical-care-progress",
          "procedure-bronch",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "critical-care-progress",
        doctorTypes: ["intensivist"],
        priority: 1,
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.pulmonology-consult-hp.label", "Pulmonology - Consult - HP"),
        value: "pulmonology-consult-hp",
        description: t("noteTypes.pulmonology-consult-hp.description", "Initial consult for pulmonologist"),
        icon: "Consult<br>HP",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "pulmonology-consult-progress",
          "patient-meeting",
          "clinic-new-cardiology",
          "clinic-followup-pulmonology",
          "code-rescue",
          "handoff",
        ],
        pushToDefault: "pulmonology-consult-progress",
        doctorTypes: ["intensivist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.pulmonology-consult-progress.label", "Pulmonology - Consult - Progress"),
        value: "pulmonology-consult-progress",
        description: t("noteTypes.pulmonology-consult-progress.description", "Progress note for pulmonologist"),
        icon: "Consult<br>Progress",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "pulmonology-consult-progress",
          "patient-meeting",
          "clinic-new-cardiology",
          "clinic-followup-pulmonology",
          "code-rescue",
          "handoff",
        ],
        pushToDefault: "pulmonology-consult-progress",
        doctorTypes: ["intensivist"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.clinic-followup-pulmonology.label", "Pulmonology - Follow-up"),
        value: "clinic-followup-pulmonology",
        description: t("noteTypes.clinic-followup-pulmonology.description", "Returning clinic visit for pulmonology"),
        icon: "Clinic<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-pulmonology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-pulmonology",
        doctorTypes: ["intensivist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.clinic-new-pulmonology.label", "Pulmonology - New patient"),
        value: "clinic-new-pulmonology",
        description: t("noteTypes.clinic-new-pulmonology.description", "New patient clinic visit for pulmonology"),
        icon: "Clinic<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-followup-pulmonology", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-followup-pulmonology",
        doctorTypes: ["intensivist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.pah-followup.label", "Pulmonology PAH - Follow-up"),
        value: "pah-followup",
        description: t("noteTypes.pah-followup.description", "Returning clinic visit for pulmonology PAH"),
        icon: "PAH<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "pah-followup", "patient-meeting", "code-rescue"],
        pushToDefault: "pah-followup",
        doctorTypes: ["intensivist"],
        summaryType: "outpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.pah-new.label", "Pulmonology PAH - New patient"),
        value: "pah-new",
        description: t("noteTypes.pah-new.description", "New patient clinic visit for pulmonology PAH"),
        icon: "PAH<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "pah-followup", "patient-meeting", "code-rescue"],
        pushToDefault: "pah-followup",
        doctorTypes: ["intensivist"],
        summaryType: "outpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.radonc-followup.label", "RadOnc - Follow-up"),
        value: "radonc-followup",
        description: t("noteTypes.radonc-followup.description", "Radiation Oncology clinic follow-up note"),
        icon: "RadOnc<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "radonc-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "radonc-followup",
        doctorTypes: ["radonc"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.radonc-new.label", "RadOnc - New patient"),
        value: "radonc-new",
        description: t("noteTypes.radonc-new.description", "Radiation Oncology clinic first visit note"),
        icon: "RadOnc<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "radonc-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "radonc-followup",
        doctorTypes: ["radonc"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.rheumatology-followup.label", "Rheumatology - Follow-up"),
        value: "rheumatology-followup",
        description: t("noteTypes.rheumatology-followup.description", "Returning clinic visit for Rheumatology"),
        icon: "Rheum<br>FollowUp",
        style: "info",
        pushTo: ["rheumatology-followup"],
        pushToDefault: "rheumatology-followup",
        doctorTypes: ["rheumatologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.rheumatology-new.label", "Rheumatology - New Patient"),
        value: "rheumatology-new",
        description: t("noteTypes.rheumatology-new.description", "New patient clinic visit for Rheumatology"),
        icon: "Rheum<br>New",
        style: "info",
        pushTo: ["rheumatology-followup"],
        pushToDefault: "rheumatology-followup",
        doctorTypes: ["rheumatologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.rehab-hp.label", "Rehab - Admit"),
        value: "rehab-hp",
        description: t("noteTypes.rehab-hp.description", "Rehab admission note"),
        icon: "Rehab<br>Admit",
        style: "info",
        pushTo: ["transition-care", "case-conference", "rehab-progress", "code-rescue", "handoff"],
        doctorTypes: ["internist"],
        summaryType: "inpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.rehab-progress.label", "Rehab - Progress"),
        value: "rehab-progress",
        description: t("noteTypes.rehab-progress.description", "Progress note for Rehab"),
        icon: "Rehab<br>Progress",
        style: "info",
        pushTo: ["transition-care", "case-conference", "rehab-progress", "code-rescue", "handoff"],
        doctorTypes: ["internist"],
        summaryType: "inpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.snf-new.label", "SNF - Admit"),
        value: "snf-new",
        description: t("noteTypes.snf-new.description", "New patient note for SNF"),
        icon: "SNF<br>Admit",
        style: "info",
        pushTo: ["transition-care", "case-conference", "snf-progress", "snf-discharge", "code-rescue", "handoff"],
        pushToDefault: "snf-progress",
        doctorTypes: ["homeHealth", "generalSubspecialty"],
        summaryType: "outpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: true,
        updatePatientData: false,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.snf-discharge.label", "SNF - Discharge"),
        value: "snf-discharge",
        description: t("noteTypes.snf-discharge.description", "Discharge note for SNF"),
        icon: "SNF<br>Discharge",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue", "handoff"],
        doctorTypes: ["homeHealth", "generalSubspecialty"],
        summaryType: "outpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: true,
        updatePatientData: false,
      },
      {
        label: t("noteTypes.snf-progress.label", "SNF - Progress"),
        value: "snf-progress",
        description: t("noteTypes.snf-progress.description", "Progress note for SNF"),
        icon: "SNF<br>Progress",
        style: "info",
        pushTo: ["transition-care", "case-conference", "snf-progress", "snf-discharge", "code-rescue", "handoff"],
        pushToDefault: "snf-progress",
        doctorTypes: ["homeHealth", "generalSubspecialty"],
        summaryType: "outpatient",
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportWounds: true,
        updatePatientData: false,
      },
      {
        label: t("noteTypes.soap.label", "SOAP"),
        value: "soap",
        description: t("noteTypes.soap.description", "SOAP note"),
        style: "info",
        icon: "SOAP",
        pushTo: "*",
        pushToDefault: "soap",
        doctorTypes: "*",
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.clinic-subspecialty.label", "Subspecialty - Follow-up"),
        value: "clinic-subspecialty",
        description: t("noteTypes.clinic-subspecialty.description", "Returning clinic visit for subspecialty"),
        icon: "Subspec<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-subspecialty", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-subspecialty",
        doctorTypes: ["generalSurgery", "generalSubspecialty", "intensivist", "neurologist"],
        summaryType: "outpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.clinic-new-subspecialty.label", "Subspecialty - New patient"),
        value: "clinic-new-subspecialty",
        description: t("noteTypes.clinic-new-subspecialty.description", "New patient clinic visit for subspecialty"),
        icon: "Subspec<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-subspecialty", "patient-meeting", "code-rescue"],
        pushToDefault: "clinic-subspecialty",
        doctorTypes: ["generalSurgery", "generalSubspecialty", "intensivist", "neurologist"],
        summaryType: "outpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.surgery-hp.label", "Surgery - Admit"),
        value: "surgery-hp",
        description: t("noteTypes.surgery-hp.description", "Initial admission documentation"),
        icon: "Surgery<br>Admit",
        style: "info",
        pushTo: ["transition-care", "case-conference", "surgery-progress", "code-rescue", "handoff"],
        pushToDefault: "surgery-progress",
        doctorTypes: ["generalSurgery"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.surgery-followup.label", "Surgery - Follow-up"),
        value: "surgery-followup",
        description: t("noteTypes.surgery-followup.description", "Returning clinic visit for surgeons"),
        icon: "Surgery<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "surgery-followup", "code-rescue", "patient-meeting"],
        pushToDefault: "surgery-followup",
        doctorTypes: ["generalSurgery"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.surgery-new.label", "Surgery - New patient"),
        value: "surgery-new",
        description: t("noteTypes.surgery-new.description", "New patient clinic visit for surgeons"),
        icon: "Surgery<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "surgery-followup", "code-rescue", "patient-meeting"],
        pushToDefault: "surgery-followup",
        doctorTypes: ["generalSurgery"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.surgery-preop.label", "Surgery - Pre-Op Eval"),
        value: "surgery-preop",
        description: t("noteTypes.surgery-preop.description", "Pre operation evaluation for surgeons"),
        icon: "Surgery<br>Pre-Op",
        style: "info",
        pushTo: ["transition-care", "case-conference", "clinic-subspecialty", "code-rescue", "patient-meeting"],
        pushToDefault: "clinic-subspecialty",
        doctorTypes: ["generalSurgery"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.surgery-progress.label", "Surgery - Progress"),
        value: "surgery-progress",
        description: t("noteTypes.surgery-progress.description", "Progress note for surgery patients"),
        icon: "Surgery<br>Progress",
        style: "info",
        pushTo: ["transition-care", "case-conference", "surgery-progress", "code-rescue", "handoff"],
        pushToDefault: "surgery-progress",
        doctorTypes: ["generalSurgery"],
        summaryType: "inpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      // {
      //   label: t("noteTypes.team-meeting.label", "Team Meeting"),
      //   value: "team-meeting",
      //   description: t(
      //     "noteTypes.team-meeting.description",
      //     "Summarize conversations with your team/colleagues with detailed summary, action points, deadlines"
      //   ),
      //   icon: "Team<br>Meeting",
      //   style: "info",
      //   doctorTypes: [
      //     "hospitalist",
      //     "internist",
      //     "er",
      //     "generalSurgery",
      //     "cardiologist",
      //     "podiatry",
      //     "nephrologist",
      //     "generalSubspecialty",
      //     "intensivist",
      //     "gastroenterologist",
      //   ],
      //   summaryType: "outpatient",
      //   priority: 3,
      //   settings: [
      //     {
      //       name: "hpiStyleMode",
      //       label: "Style",
      //       variable: "hpi",
      //       variableLabel: "HPI",
      //       values: [
      //         {
      //           label: "Paragraph",
      //           value: "paragraph",
      //         },
      //         {
      //           label: "Bullet Points",
      //           value: "bullets",
      //         },
      //       ],
      //       defaultValue: "paragraph",
      //     },
      //     {
      //       name: "hpiLengthMode",
      //       label: "Length",
      //       variable: "hpi",
      //       variableLabel: "HPI",
      //       values: [
      //         {
      //           label: "Concise",
      //           value: "concise",
      //         },
      //         {
      //           label: "Comprehensive",
      //           value: "comprehensive",
      //         },
      //       ],
      //       defaultValue: "comprehensive",
      //     },
      //     {
      //       name: "mdmStyleMode",
      //       label: "Style",
      //       variable: "medicalDecisionMaking",
      //       variableLabel: "Medical Decision Making",
      //       values: [
      //         {
      //           label: "Paragraph",
      //           value: "paragraph",
      //         },
      //         {
      //           label: "Bullet Points",
      //           value: "bullets",
      //         },
      //       ],
      //       defaultValue: "paragraph",
      //     },
      //     {
      //       name: "mdmLengthMode",
      //       label: "Length",
      //       variable: "medicalDecisionMaking",
      //       variableLabel: "Medical Decision Making",
      //       values: [
      //         {
      //           label: "Concise",
      //           value: "concise",
      //         },
      //         {
      //           label: "Comprehensive",
      //           value: "comprehensive",
      //         },
      //       ],
      //       defaultValue: "comprehensive",
      //     },
      //     {
      //       name: "rosMode",
      //       label: "Length",
      //       variable: "ros",
      //       variableLabel: "Review of Systems",
      //       values: [
      //         {
      //           label: "Concise",
      //           value: "concise",
      //         },
      //         {
      //           label: "Complex",
      //           value: "complex",
      //         },
      //       ],
      //       defaultValue: "concise",
      //     },
      //     {
      //       name: "physicalExamLengthMode",
      //       label: "Length",
      //       variable: "physicalExam",
      //       variableLabel: "Physical Exam",
      //       values: [
      //         {
      //           label: "Concise",
      //           value: "concise",
      //         },
      //         {
      //           label: "Comprehensive",
      //           value: "comprehensive",
      //         },
      //       ],
      //       defaultValue: "comprehensive",
      //     },
      //     {
      //       name: "planOfCareMode",
      //       label: "Length",
      //       variable: "planOfCare",
      //       variableLabel: "Plan Of Care",
      //       values: [
      //         {
      //           label: "Concise",
      //           value: "concise",
      //         },
      //         {
      //           label: "Problem Based",
      //           value: "complex",
      //         },
      //       ],
      //       defaultValue: "complex",
      //     },
      //   ],
      // },
      {
        label: t("noteTypes.clinic-telemed.label", "Telemed"),
        value: "clinic-telemed",
        description: t("noteTypes.clinic-telemed.description", "Telemedicine note"),
        icon: "Clinic<br>Telemed",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "surgery-preop",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
        ],
        pushToDefault: "clinic",
        doctorTypes: [
          "primaryCare",
          "internist",
          "generalSurgery",
          "cardiologist",
          "nephrologist",
          "podiatry",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "neuroOrthoSpine",
          "radonc",
          "orthopedicSurgeon",
        ],
        summaryType: "outpatient",
        priority: 1,
        // Mirrors backend `DynaFormat - Outpatient - Telemed.json` section list (visible-by-default
        // sections only — sectionType="none", readOnly, and hideIfEmpty entries omitted).
        skeletonTitles: [
          "Chief Complaint",
          "History of Present Illness",
          "A/V Modality",
          "Past Medical History",
          "Past Surgical History",
          "Allergies",
          "Current Medications",
          "Family History",
          "Social History",
          "Review of Systems (ROS)",
          "Physical Exams",
          "Assessment and Plan",
          "Problem List",
          "Plan Of Care",
          "Referrals",
          "Education/Counseling",
          "Summary of Prescriptions and Orders",
          "Checklist",
          "Follow up",
          "Patient complexity",
          "Level of Service",
          "ICD 10 Codes",
          "Time Spent",
        ],
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.clinic-telemed-audio.label", "Telemed Audio only"),
        value: "clinic-telemed-audio",
        description: t("noteTypes.clinic-telemed-audio.description", "Telemedicine audio only note"),
        icon: "Tele",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "surgery-preop",
          "neuro-ortho-spine-followup",
          "neuro-ortho-spine-preop",
        ],
        pushToDefault: "clinic",
        doctorTypes: [
          "primaryCare",
          "internist",
          "generalSurgery",
          "cardiologist",
          "nephrologist",
          "podiatry",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "neuroOrthoSpine",
          "radonc",
          "orthopedicSurgeon",
        ],
        summaryType: "outpatient",
        priority: 1,
        // Mirrors backend `DynaFormat - Outpatient - Telemed _audio only_.json` section list.
        skeletonTitles: [
          "Chief Complaint",
          "History of Present Illness",
          "Past Medical History",
          "Past Surgical History",
          "Allergies",
          "Current Medications",
          "Family History",
          "Social History",
          "Review of Systems (ROS)",
          "Assessment and Plan",
          "Plan Of Care",
          "Referrals",
          "Education/Counseling",
          "Summary of Prescriptions and Orders",
          "Follow up",
          "ICD 10 Codes",
          "Level of Service",
          "Time Spent",
        ],
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.test.label", "Test"),
        value: "test",
        description: t("noteTypes.test.description", "Generic test note"),
        style: "info",
        icon: "Note",
        pushTo: [],
        pushToDefault: null,
        doctorTypes: [
          "primaryCare",
          "internist",
          "generalSurgery",
          "cardiologist",
          "nephrologist",
          "podiatry",
          "generalSubspecialty",
          "intensivist",
          "gastroenterologist",
          "neuroOrthoSpine",
          "radonc",
          "orthopedicSurgeon",
        ],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        userRoles: ["administrator", "supervisor"],
      },
      {
        label: t("noteTypes.therapy-followup.label", "Therapy - Follow-up"),
        value: "therapy-followup",
        description: t("noteTypes.therapy-followup.description", "Behavioral therapy followup note"),
        icon: "Therapy<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "psych-icanotes",
          "psych-followup",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "therapy-followup",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.therapy-intake.label", "Therapy - Intake"),
        value: "therapy-intake",
        description: t("noteTypes.therapy-intake.description", "Behavioral therapy intake note"),
        icon: "Therapy<br>Intake",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "psych-followup",
          "psych-girp",
          "psych-birp",
          "psych-icanotes",
          "psych-followup",
          "therapy-intake",
          "therapy-followup",
          "code-rescue",
        ],
        pushToDefault: "therapy-followup",
        doctorTypes: ["behavioral"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
      },
      {
        label: t("noteTypes.transition-care.label", "Transition of Care"),
        value: "transition-care",
        description: t("noteTypes.transition-care.description", "Transition of Care"),
        style: "info",
        icon: "Trans<br>Care",
        pushTo: [
          "transition-care",
          "transitionalist-followup",
          "case-conference",
          "soap",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "woundcare-home-visit",
          "alf-home-new",
          "alf-home-followup",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "soap",
        doctorTypes: ["homeHealth"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.transitionalist-followup.label", "Transitionalist Follow up"),
        value: "transitionalist-followup",
        description: t(
          "noteTypes.transitionalist-followup.description",
          "Follow-up care for patients transitioning between care settings"
        ),
        icon: "Trans<br>F/U",
        style: "info",
        pushTo: [
          "transitionalist-followup",
          "transition-care",
          "case-conference",
          "patient-meeting",
          "code-rescue",
          "clinic",
          "soap",
          "clinic-telemed",
          "clinic-telemed-audio",
          "woundcare-followup",
          "woundcare-home-visit",
        ],
        pushToDefault: "transitionalist-followup",
        doctorTypes: ["homeHealth", "primaryCare", "internist"],
        summaryType: "outpatient",
        supportVitals: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        priority: 2,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.urgent-care-simplified.label", "Urgent Care - Simplified"),
        value: "urgent-care-simplified",
        description: t("noteTypes.urgent-care-simplified.description", "Urgent Care simplified note"),
        icon: "Urgent",
        style: "info",
        pushTo: ["transition-care", "case-conference", "code-rescue", "patient-meeting"],
        pushToDefault: "code-rescue",
        doctorTypes: ["er"],
        summaryType: "outpatient",
        priority: 1,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        supportVitals: true,
        supportIncidents: false,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.urology-followup.label", "Urology - Follow-up"),
        value: "urology-followup",
        description: t("noteTypes.urology-followup.description", "Returning clinic visit for urology"),
        icon: "Urology<br>FollowUp",
        style: "info",
        pushTo: ["transition-care", "case-conference", "urology-followup", "code-rescue", "patient-meeting"],
        pushToDefault: "urology-followup",
        doctorTypes: ["urologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.urology-new.label", "Urology - New patient"),
        value: "urology-new",
        description: t("noteTypes.urology-new.description", "New patient clinic visit for urology"),
        icon: "Urology<br>New",
        style: "info",
        pushTo: ["transition-care", "case-conference", "urology-followup", "code-rescue", "patient-meeting"],
        pushToDefault: "urology-followup",
        doctorTypes: ["urologist"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.well-woman.label", "Well Woman"),
        value: "well-woman",
        description: t("noteTypes.well-woman.description", "Annual well woman screening visit"),
        icon: "Well<br>Woman",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "obstetrics-followup",
          "gynecology-followup",
          "code-rescue",
        ],
        pushToDefault: "clinic",
        doctorTypes: ["obgyn", "primaryCare"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.well-woman-gyn.label", "Well Woman GYN"),
        value: "well-woman-gyn",
        description: t("noteTypes.well-woman-gyn.description", "Annual well woman gynecology screening visit"),
        icon: "GYN Well<br>Woman",
        style: "info",
        pushTo: ["transition-care", "case-conference", "well-woman-gyn", "code-rescue"],
        pushToDefault: "well-woman-gyn",
        doctorTypes: ["obgyn"],
        summaryType: "outpatient",
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
        updatePatientData: true,
        supportUploadDocuments: true,
      },
      {
        label: t("noteTypes.woundcare-followup.label", "Wound Care - Follow-up"),
        value: "woundcare-followup",
        description: t("noteTypes.woundcare-followup.description", "Follow-up visit for wound care"),
        icon: "Wound<br>FollowUp",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "woundcare-followup",
          "woundcare-home-visit",
          "woundcare-procedure",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "woundcare-followup",
        doctorTypes: ["woundCare", "generalSurgery", "podiatry"],
        supportWounds: true,
        summaryType: "outpatient",
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.woundcare-home-visit.label", "Wound Care - Home visit"),
        value: "woundcare-home-visit",
        description: t("noteTypes.woundcare-home-visit.description", "Wound care home visit"),
        icon: "Wound<br>Home V.",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "woundcare-home-visit",
          "woundcare-procedure",
          "woundcare-followup",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "woundcare-home-visit",
        doctorTypes: ["homeHealth", "woundCare", "generalSurgery", "podiatry"],
        supportWounds: true,
        summaryType: "outpatient",
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      {
        label: t("noteTypes.woundcare-new.label", "Wound Care - New patient"),
        value: "woundcare-new",
        description: t("noteTypes.woundcare-new.description", "New patient visit for wound care"),
        icon: "Wound<br>New",
        style: "info",
        pushTo: [
          "transition-care",
          "case-conference",
          "woundcare-followup",
          "woundcare-home-visit",
          "woundcare-procedure",
          "clinic",
          "clinic-telemed",
          "clinic-telemed-audio",
          "clinic-phone",
          "patient-meeting",
          "code-rescue",
        ],
        pushToDefault: "woundcare-followup",
        doctorTypes: ["woundCare", "generalSurgery", "podiatry"],
        summaryType: "outpatient",
        supportWounds: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        settings: [
          {
            name: "hpiStyleMode",
            label: "Style",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "hpiLengthMode",
            label: "Length",
            variable: "hpi",
            variableLabel: "HPI",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "mdmStyleMode",
            label: "Style",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Paragraph",
                value: "paragraph",
              },
              {
                label: "Bullet Points",
                value: "bullets",
              },
            ],
            defaultValue: "paragraph",
          },
          {
            name: "mdmLengthMode",
            label: "Length",
            variable: "medicalDecisionMaking",
            variableLabel: "Medical Decision Making",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "rosMode",
            label: "Length",
            variable: "ros",
            variableLabel: "Review of Systems",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Complex",
                value: "complex",
              },
            ],
            defaultValue: "concise",
          },
          {
            name: "physicalExamLengthMode",
            label: "Length",
            variable: "physicalExam",
            variableLabel: "Physical Exam",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Comprehensive",
                value: "comprehensive",
              },
            ],
            defaultValue: "comprehensive",
          },
          {
            name: "planOfCareMode",
            label: "Length",
            variable: "planOfCare",
            variableLabel: "Plan Of Care",
            values: [
              {
                label: "Concise",
                value: "concise",
              },
              {
                label: "Problem Based",
                value: "complex",
              },
            ],
            defaultValue: "complex",
          },
        ],
      },
      // Chiropractic note types (source of truth: src/chiro/notes/catalogs/noteTypes.js).
      // Mapped to the core note-type shape and tagged for the chiropractor doctorType so
      // noteTypesMap resolves their labels everywhere (fixes "note type not found").
      ...CHIRO_NOTE_TYPES.map((c) => ({
        value: c.value,
        label: c.label,
        description: c.desc || c.description || c.label,
        doctorTypes: ["chiropractor"],
        newEncounter: c.newEncounter !== false,
        summaryType: "outpatient",
      })),
    ];

    // Inject custom notes from user profile + org level (ultimate accounts)
    const userCustomNotes = auth?.auth?.user?.custom?.customNotes || [];
    const orgCustomNotes = auth?.auth?.orgInfo?.customNotes || [];
    const seenIds = new Set();
    const allCustomNotes = [...orgCustomNotes, ...userCustomNotes].filter((cn) => {
      if (seenIds.has(cn.id)) return false;
      seenIds.add(cn.id);
      return true;
    });
    for (const cn of allCustomNotes) {
      initNotes.push({
        value: "custom:" + cn.id,
        label: cn.name,
        description: cn.description || t("noteTypes.custom.description", "Custom note template"),
        icon: "Custom",
        style: "secondary",
        isCustomNote: true,
        customNoteId: cn.id,
        summaryType: cn.clinicalSetting || "outpatient",
        doctorTypes: "*",
        priority: 1,
        newEncounter: true,
        updatePatientData: true,
        supportUploadDocuments: true,
        supportVitals: true,
      });
    }

    // Normalize doctorTypes in each noteType
    const normalizedNotes = initNotes
      .filter((note) => {
        if (note.value === "test") {
          const organization = auth?.auth?.session?.currentOrganizationName?.toLowerCase() ?? "";
          const environment = process.env.REACT_APP_ENV;

          return organization === "beta" || environment === "staging" || environment === "local";
        }

        return true;
      })
      .map((note) => ({
        ...note,
        doctorTypes: Array.isArray(note.doctorTypes) ? note.doctorTypes.map((dt) => normalizeDoctorType(dt)) : note.doctorTypes,
      }));

    const noteTypesMap = normalizedNotes?.reduce(function (map, obj) {
      map[obj.value] = obj;
      return map;
    }, {});

    return { noteTypesMap, noteTypes: normalizedNotes };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, auth?.auth?.session?.currentOrganizationName, customNotesKey]);

  function getDefaultNotesByDoctorType(doctorType) {
    let defNewNote = null;
    let defImportNote = null;

    switch (doctorType) {
      case "hospitalist":
      case "internist":
        defNewNote = "history";
        defImportNote = "progress";
        break;
      default:
      case "primaryCare":
        defNewNote = "clinic";
        defImportNote = "clinic";
        break;
      case "allergyImmunology":
        defNewNote = "allergy-followup";
        defImportNote = "allergy-followup";
        break;
      case "er":
        defNewNote = "er";
        defImportNote = "er";
        break;
      case "paramedics":
        defNewNote = "paramedics";
        defImportNote = "paramedics";
        break;
      case "generalSurgery":
        defNewNote = "clinic-subspecialty";
        defImportNote = "clinic-subspecialty";
        break;
      case "generalSubspecialty":
        defNewNote = "clinic-subspecialty";
        defImportNote = "clinic-subspecialty";
        break;
      case "cardiologist":
        defNewNote = "clinic-followup-cardiology";
        defImportNote = "clinic-followup-cardiology";
        break;
      case "podiatry":
        defNewNote = "clinic-followup-podiatry";
        defImportNote = "clinic-followup-podiatry";
        break;
      case "nephrologist":
        defNewNote = "clinic-followup-nephrology";
        defImportNote = "clinic-followup-nephrology";
        break;
      case "neurologist":
        defNewNote = "neuro-followup";
        defImportNote = "neuro-followup";
        break;
      case "intensivist":
        defNewNote = "critical-care-hp";
        defImportNote = "critical-care-progress";
        break;
      case "dentist":
        defNewNote = "dental-care-progress";
        defImportNote = "dental-care-progress";
        break;
      case "dermatology":
        defNewNote = "derm-followup";
        defImportNote = "derm-followup";
        break;
      case "behavioral":
        defNewNote = "psych-intake";
        defImportNote = "psych-intake";
        break;
      case "urologist":
        defNewNote = "urology-followup";
        defImportNote = "urology-followup";
        break;
      case "neuroOrthoSpine":
        defNewNote = "neuro-ortho-spine-followup";
        defImportNote = "neuro-ortho-spine-followup";
        break;
      case "radonc":
        defNewNote = "radonc-followup";
        defImportNote = "radonc-followup";
        break;
      case "rheumatologist":
        defNewNote = "rheumatology-followup";
        defImportNote = "rheumatology-followup";
        break;
      case "endocrinologist":
        defNewNote = "endocrinology-followup";
        defImportNote = "endocrinology-followup";
        break;
      case "gastroenterologist":
        defNewNote = "clinic-followup-gastroenterology";
        defImportNote = "clinic-followup-gastroenterology";
        break;
      case "infectiousDiseases":
        defNewNote = "infection-followup";
        defImportNote = "infection-followup";
        break;
      case "obgyn":
        defNewNote = "gynecology-followup";
        defImportNote = "gynecology-followup";
        break;
      case "orthopedicSurgeon":
        defNewNote = "ortho-injury-followup";
        defImportNote = "ortho-injury-followup";
        break;
      case "painmgmt":
        defNewNote = "painmgmt-followup";
        defImportNote = "painmgmt-followup";
        break;
      case "otolaryngologists":
        defNewNote = "ent-followup";
        defImportNote = "ent-followup";
        break;
      case "pediatrician":
        defNewNote = "pediatric-wellchild-followup";
        defImportNote = "pediatric-wellchild-followup";
        break;
      case "physicalTherapist":
        defNewNote = "pt-followup";
        defImportNote = "pt-followup";
        break;
      case "homeHealth":
        defNewNote = "oasis-start";
        defImportNote = "oasis-start";
        break;
      case "woundCare":
        defNewNote = "woundcare-followup";
        defImportNote = "woundcare-followup";
        break;
      case "chiropractor":
        defNewNote = "chiro-intake";
        defImportNote = "chiro-fu";
        break;
    }

    return { newNote: defNewNote, importNote: defImportNote };
  }

  // lucasDoctorTypes
  const doctorTypes = {
    allergyImmunology: t("doctorTypes.allergyImmunology"),
    behavioral: t("doctorTypes.behavioral"),
    cardiologist: t("doctorTypes.cardiologist"),
    chiropractor: t("doctorTypes.chiropractor"),
    dentist: t("doctorTypes.dentist"),
    dermatology: t("doctorTypes.dermatology"),
    endocrinologist: t("doctorTypes.endocrinologist"),
    er: t("doctorTypes.er"),
    gastroenterologist: t("doctorTypes.gastroenterologist"),
    generalSubspecialty: t("doctorTypes.generalSubspecialty"),
    generalSurgery: t("doctorTypes.generalSurgery"),
    hospitalist: t("doctorTypes.hospitalist"),
    homeHealth: t("doctorTypes.homeHealth"),
    infectiousDiseases: t("doctorTypes.infectiousDiseases"),
    intensivist: t("doctorTypes.intensivist"),
    internist: t("doctorTypes.internist"),
    nephrologist: t("doctorTypes.nephrologist"),
    neurologist: t("doctorTypes.neurologist"),
    neuroOrthoSpine: t("doctorTypes.neuroOrthoSpine"),
    obgyn: t("doctorTypes.obgyn"),
    orthopedicSurgeon: t("doctorTypes.orthopedicSurgeon"),
    otolaryngologists: t("doctorTypes.otolaryngologists"),
    painmgmt: t("doctorTypes.painmgmt"),
    paramedics: t("doctorTypes.paramedics"),
    pediatrician: t("doctorTypes.pediatrician"),
    podiatry: t("doctorTypes.podiatry"),
    physicalTherapist: t("doctorTypes.physicalTherapist"),
    primaryCare: t("doctorTypes.primaryCare"),
    radonc: t("doctorTypes.radonc"),
    rheumatologist: t("doctorTypes.rheumatologist"),
    urologist: t("doctorTypes.urologist"),
    woundCare: t("doctorTypes.woundCare"),
  };

  const states = [
    { code: "AL", name: "Alabama" },
    { code: "AK", name: "Alaska" },
    { code: "AZ", name: "Arizona" },
    { code: "AR", name: "Arkansas" },
    { code: "CA", name: "California" },
    { code: "CO", name: "Colorado" },
    { code: "CT", name: "Connecticut" },
    { code: "DE", name: "Delaware" },
    { code: "FL", name: "Florida" },
    { code: "GA", name: "Georgia" },
    { code: "HI", name: "Hawaii" },
    { code: "ID", name: "Idaho" },
    { code: "IL", name: "Illinois" },
    { code: "IN", name: "Indiana" },
    { code: "IA", name: "Iowa" },
    { code: "KS", name: "Kansas" },
    { code: "KY", name: "Kentucky" },
    { code: "LA", name: "Louisiana" },
    { code: "ME", name: "Maine" },
    { code: "MD", name: "Maryland" },
    { code: "MA", name: "Massachusetts" },
    { code: "MI", name: "Michigan" },
    { code: "MN", name: "Minnesota" },
    { code: "MS", name: "Mississippi" },
    { code: "MO", name: "Missouri" },
    { code: "MT", name: "Montana" },
    { code: "NE", name: "Nebraska" },
    { code: "NV", name: "Nevada" },
    { code: "NH", name: "New Hampshire" },
    { code: "NJ", name: "New Jersey" },
    { code: "NM", name: "New Mexico" },
    { code: "NY", name: "New York" },
    { code: "NC", name: "North Carolina" },
    { code: "ND", name: "North Dakota" },
    { code: "OH", name: "Ohio" },
    { code: "OK", name: "Oklahoma" },
    { code: "OR", name: "Oregon" },
    { code: "PA", name: "Pennsylvania" },
    { code: "RI", name: "Rhode Island" },
    { code: "SC", name: "South Carolina" },
    { code: "SD", name: "South Dakota" },
    { code: "TN", name: "Tennessee" },
    { code: "TX", name: "Texas" },
    { code: "UT", name: "Utah" },
    { code: "VT", name: "Vermont" },
    { code: "VA", name: "Virginia" },
    { code: "WA", name: "Washington" },
    { code: "WV", name: "West Virginia" },
    { code: "WI", name: "Wisconsin" },
    { code: "WY", name: "Wyoming" },
    { code: "DC", name: "District of Columbia" },
    { code: "AS", name: "American Samoa" },
    { code: "GU", name: "Guam" },
    { code: "MP", name: "Northern Mariana Islands" },
    { code: "PR", name: "Puerto Rico" },
    { code: "UM", name: "United States Minor Outlying Islands" },
    { code: "VI", name: "Virgin Islands, U.S." },
  ];

  const docTypes = [
    {
      value: "images",
      label: "Radiology",
    },
    {
      value: "meds",
      label: "Medications",
    },
    {
      value: "labs",
      label: "Labs",
    },
    {
      value: "records",
      label: "Records",
    },
    {
      value: "wounds",
      label: "Wounds",
    },
    {
      value: "others",
      label: "Others",
    },
    {
      value: "order-forms",
      label: "Order Forms",
    },
    {
      value: "hcfa-forms",
      label: "HCFA Forms",
    },
    {
      value: "wound-photos",
      label: "Wound Photos",
    },
    {
      value: "eob",
      label: "EOB",
    },
    {
      value: "invoices",
      label: "Invoices",
    },
    {
      value: "insurance-card",
      label: "Insurance Card",
    },
    {
      value: "billing-worksheet",
      label: "Billing Worksheet",
    },
    { value: "vob", label: "VOB" },
    {
      value: "clinical-documentation",
      label: "Clinical Documentation",
    },
    {
      value: "face-sheet",
      label: "Face Sheet",
    },
  ];

  const noteSettings = [
    {
      name: "hpiStyleMode",
      label: "Style",
      variable: "hpi",
      variableLabel: "HPI",
      values: [
        {
          label: "Paragraph",
          value: "paragraph",
        },
        {
          label: "Bullet Points",
          value: "bullets",
        },
      ],
      defaultValue: "paragraph",
    },
    {
      name: "hpiLengthMode",
      label: "Length",
      variable: "hpi",
      variableLabel: "HPI",
      values: [
        {
          label: "Concise",
          value: "concise",
        },
        {
          label: "Comprehensive",
          value: "comprehensive",
        },
      ],
      defaultValue: "comprehensive",
    },
    {
      name: "mdmStyleMode",
      label: "Style",
      variable: "medicalDecisionMaking",
      variableLabel: "Medical Decision Making",
      values: [
        {
          label: "Paragraph",
          value: "paragraph",
        },
        {
          label: "Bullet Points",
          value: "bullets",
        },
      ],
      defaultValue: "paragraph",
    },
    {
      name: "mdmLengthMode",
      label: "Length",
      variable: "medicalDecisionMaking",
      variableLabel: "Medical Decision Making",
      values: [
        {
          label: "Concise",
          value: "concise",
        },
        {
          label: "Comprehensive",
          value: "comprehensive",
        },
      ],
      defaultValue: "comprehensive",
    },
    {
      name: "rosMode",
      label: "Length",
      variable: "ros",
      variableLabel: "Review of Systems",
      values: [
        {
          label: "Concise",
          value: "concise",
        },
        {
          label: "Complex",
          value: "complex",
        },
      ],
      defaultValue: "concise",
    },
    {
      name: "physicalExamLengthMode",
      label: "Length",
      variable: "physicalExam",
      variableLabel: "Physical Exam",
      values: [
        {
          label: "Concise",
          value: "concise",
        },
        {
          label: "Comprehensive",
          value: "comprehensive",
        },
      ],
      defaultValue: "comprehensive",
    },
    {
      name: "planOfCareMode",
      label: "Length",
      variable: "planOfCare",
      variableLabel: "Plan Of Care",
      values: [
        {
          label: "Concise",
          value: "concise",
        },
        {
          label: "Problem Based",
          value: "complex",
        },
      ],
      defaultValue: "complex",
    },
  ];

  return {
    docTypes,
    noteTypes,
    noteTypesMap,
    getDefaultNotesByDoctorType,
    doctorTypes,
    noteSettings,
    states,
  };
}

export function ProvideNoteType({ children }) {
  const values = useNoteTypeProvider();
  return <noteTypeContext.Provider value={values}>{children}</noteTypeContext.Provider>;
}

export const useNoteType = () => {
  return useContext(noteTypeContext);
};
